/*
  Purekey: stran izdelka — chipi okusov v Mix & Save (Adoric) kot chipi okusov nad njim (PLAN 5.1 · P4).
  Adoric riše v odprt shadow DOM: CSS teme vanj seže samo s spremenljivkami --adoric-* (pk-product.css),
  ime okusa pa je na chipu samo v besedilu. Skripta zato v shadow DOM doda list z obliko chipov
  (Forma, velike črke, 34 px, izbran 700) in vsakemu chipu nastavi barvo izbranega okusa.
  Barvo prebere iz pravil .swatch-element.<handle> v pk-product.css (EN in SL), zato je tabela okusov ena;
  okus brez barve ostane temen. Ob menjavi variante Adoric element ostane, ob novem izrisu chipov
  (npr. izbira paketa) jih opazovalec znova pobarva.
  Naslov paketa (»Mix your flavours. Save up to 20%.«) Adoric v drugem jeziku ne prevede, čeprav ima prevod
  vpisan, zato ga skripta zamenja s tem prevodom (glej translateHeading).
*/
(() => {
  const CHIP_CSS = `
    :host .labels .labels__item {
      box-sizing: border-box;
      height: 34px;
      padding: 0 16px;
    }

    :host .labels__item .labels__text {
      font-family: var(--pk-font-ui);
      line-height: 1;
      letter-spacing: 0.02em;
      text-transform: uppercase;
    }

    :host .labels__item.selected .labels__text {
      font-weight: 700;
    }

    @media (hover: hover) {
      :host .labels__item:not(.selected):hover::before {
        background-color: #ededed;
      }
    }
  `;

  // Prehod barve se vklopi šele po prvem barvanju, sicer izbran chip ob nalaganju preide iz temne barve v barvo okusa
  const CHIP_TRANSITION_CSS = `
    @media (prefers-reduced-motion: no-preference) {
      :host .labels .labels__item {
        transition: color 0.2s ease;
      }

      :host .labels .labels__item::before {
        transition: background-color 0.2s ease;
      }
    }
  `;

  const colors = new Map();
  const initialized = new WeakSet();

  // Kot Shopifyjev handle: »Češnja« → cesnja, »Limona limeta« → limona-limeta
  const handleize = (text) =>
    text
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  const flavourColor = (handle) => {
    if (!colors.has(handle)) {
      const probe = document.createElement('span');
      probe.className = `swatch-element ${handle}`;
      probe.hidden = true;
      document.body.appendChild(probe);
      colors.set(handle, getComputedStyle(probe).getPropertyValue('--pk-chip-active').trim());
      probe.remove();
    }
    return colors.get(handle);
  };

  // Adoric ozadje izbranega chipa riše v ::before iz --selected-background-color, ki ga nastavi na vrstici
  const paintChips = (root) => {
    root.querySelectorAll('.labels__item').forEach((item) => {
      const handle = handleize((item.querySelector('.labels__text') || item).textContent);
      if (item.dataset.pkFlavour === handle) return;
      item.dataset.pkFlavour = handle;

      const color = handle ? flavourColor(handle) : '';
      if (color) {
        item.style.setProperty('--selected-background-color', color);
      } else {
        item.style.removeProperty('--selected-background-color');
      }
    });
  };

  // Adoric prevod naslova (ključ state.design.bundle.heading.text v atributu language-modificator) zapiše v
  // nastavitve, iz katerih naslova ne izriše, zato ostane angleški. Besedilo vzamemo iz tega prevoda,
  // vrednosti oznak, npr. {{MAX_DISCOUNT}}, pa iz izrisanega angleškega naslova.
  const HEADING_KEY = 'state.design.bundle.heading.text';

  const headingTranslation = (host) => {
    try {
      const language = host.getAttribute('language');
      const entry = JSON.parse(host.getAttribute('language-modificator') || '[]').find((item) => item.key === language);
      const translated = entry && entry.value && entry.value[HEADING_KEY] && entry.value[HEADING_KEY].content;
      const original = JSON.parse(host.getAttribute('design') || '{}').bundle.heading.text;
      if (!translated || !original || translated === original) return null;

      const tokens = [];
      const pattern = original
        .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        .replace(/\\\{\\\{(\w+)\\\}\\\}/g, (match, token) => {
          tokens.push(token);
          return '(.+?)';
        });
      return { translated, tokens, regex: new RegExp(`^${pattern}$`) };
    } catch (error) {
      return null;
    }
  };

  // Zamenja se samo besedilni vozel, ki ga je izrisal Adoric, zato ga ob ponovnem izrisu spet najdemo
  const translateHeading = (root, heading) => {
    root.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach((element) => {
      element.childNodes.forEach((node) => {
        if (node.nodeType !== Node.TEXT_NODE) return;
        const match = node.data.trim().match(heading.regex);
        if (!match) return;
        node.data = heading.tokens.reduce((text, token, index) => text.split(`{{${token}}}`).join(match[index + 1]), heading.translated);
      });
    });
  };

  const setup = (host, tries = 50) => {
    if (initialized.has(host)) return;

    const root = host.shadowRoot;
    if (!root) {
      if (tries > 0) setTimeout(() => setup(host, tries - 1), 100);
      return;
    }
    initialized.add(host);

    const style = document.createElement('style');
    style.textContent = CHIP_CSS;
    root.appendChild(style);

    const heading = headingTranslation(host);
    const update = () => {
      paintChips(root);
      if (heading) translateHeading(root, heading);
    };
    update();
    new MutationObserver(update).observe(root, { childList: true, subtree: true, characterData: true });
    requestAnimationFrame(() => requestAnimationFrame(() => {
      style.textContent += CHIP_TRANSITION_CSS;
    }));
  };

  customElements.whenDefined('adoric-bundle-mark-six').then(() => {
    document.querySelectorAll('adoric-bundle-mark-six').forEach((host) => setup(host));
  });
})();

/*
  Pilli iz oznak (`_pill:`, `_flavour:`) na sliki izdelka, enaki kot na karticah Shopa.
  Blok `pk_pills` jih izriše v stolpcu z opisom, tu se prestavijo v Flowov okvir značke
  (`[data-product-badge]`, ločen za namizje in mobilno). Stare oznake `_badge:` ostanejo
  na izdelku, njihova siva značka se skrije v `pk-product.css`.
*/
(() => {
  // Predlogo shranimo, ker Flow ob menjavi variante galerijo izriše na novo in okvir značke nastane znova
  let template = null;

  const place = () => {
    if (!template) {
      const source = document.querySelector('[data-pk-pills]');
      if (!source) return;
      template = source.cloneNode(true);
      source.remove();
    }
    document.querySelectorAll('[data-product-badge]').forEach((target) => {
      if (target.querySelector('[data-pk-pills]')) return;
      const pills = template.cloneNode(true);
      pills.setAttribute('data-pk-placed', '');
      target.prepend(pills);
      target.classList.add('pk-has-pills');
    });
  };

  // Pill izbranega okusa: na karticah Shopa je okus viden, tu ga beremo z izbranega Flowovega swatcha
  // (`data-option`, `data-value`) in pobarvamo iz `--pk-chip-active` v pk-product.css, kot chipe Adorica.
  const FLAVOUR_OPTIONS = ['flavour', 'flavor', 'okus'];
  const colors = new Map();

  const handleize = (text) =>
    (text || '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

  const flavourColor = (handle) => {
    if (!colors.has(handle)) {
      const probe = document.createElement('span');
      probe.className = `swatch-element ${handle}`;
      probe.hidden = true;
      document.body.appendChild(probe);
      colors.set(handle, getComputedStyle(probe).getPropertyValue('--pk-chip-active').trim());
      probe.remove();
    }
    return colors.get(handle);
  };

  const syncFlavour = () => {
    const checked = document.querySelector('.swatch-element input:checked');
    const swatch = checked && checked.closest('.swatch-element');
    if (!swatch || !FLAVOUR_OPTIONS.includes(handleize(swatch.dataset.option))) return;

    const label = (swatch.dataset.value || '').trim();
    if (!label) return;
    const color = flavourColor(handleize(label));

    document.querySelectorAll('[data-product-badge] [data-pk-pills]').forEach((pills) => {
      // Izdelki z oznako `_flavour:` imajo pill okusa že iz Liquida
      if (pills.querySelector('.pk-pill--flavour:not([data-pk-variant])')) return;

      let pill = pills.querySelector('[data-pk-variant]');
      if (!pill) {
        pill = document.createElement('span');
        pill.className = 'pk-pill pk-pill--sm pk-pill--flavour';
        pill.setAttribute('data-pk-variant', '');
        pills.appendChild(pill);
      }
      if (pill.textContent !== label) pill.textContent = label;
      if (color) {
        if (pill.style.backgroundColor !== color) pill.style.backgroundColor = color;
      } else {
        pill.style.removeProperty('background-color');
      }
    });
  };

  const start = () => {
    place();
    syncFlavour();
    document.addEventListener('change', (event) => {
      if (event.target.closest('.swatch-element')) syncFlavour();
    });
    const section = document.querySelector('.product-section');
    if (section) {
      new MutationObserver(() => {
        place();
        syncFlavour();
      }).observe(section, { childList: true, subtree: true });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
